import { pick, get } from 'lodash';
import { useTableQuery } from '@/composables/useTableQuery';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';
import { t } from '@/composables/useI18n';
import { getEntityNameApiRequest } from '@/services/api/entity-api';
import { DelimitersEnum } from '@/services/models/Enums';

const namePromisesCollection: Record<string, Promise<unknown>> = {};

type EntityNameResult = {
  data: {
    payload: Record<string, unknown>;
  };
};

export type ExpandedReportQueryDrill = Bemob.ReportQueryDrill & {
  parentFilterValues?: string;
};

export type ReportNavigationOptions = {
  isAddTab?: boolean;
  isReplaceTab?: boolean;
  isInit?: boolean;
  tabData?: Bemob.TabData;
};

export type ReportUtilsContext = {
  drill?: unknown[];
  getReturnLink?: (id: string) => string;
  reportTabLink?: string;
};

export const useReportUtils = (context: ReportUtilsContext = {}) => {
  const store = useStore();
  const tableQueryApi = useTableQuery();

  const errorToast = (message: string, options: unknown) => {
    $emitter.emit('toast-error', message, options);
  };

  const isUUID = (data: string) =>
    new RegExp(/([0-9a-f]+)-([0-9a-f]+)-([0-9a-f]+)-([0-9a-f]+)-([0-9a-f]+)$/).test(data);

  const cacheResponse = (id: unknown, data: unknown) => {
    store.setTableEntityName({
      key: id,
      value: data,
    });
  };

  const getCachedResponse = (id: unknown) => tableQueryApi.tableEntityNames.value[id as string];

  const tabReturnLink = (id: string) => {
    const activeReports = tableQueryApi.tableActiveReports.value;
    const index = activeReports.map((report) => report.id).indexOf(id);
    if (index >= 1) {
      const previous = activeReports[index - 1];
      if (previous) return `${previous.url}?exitReport=${id}`;
    }
    if (index === -1) {
      const previous = activeReports[activeReports.length - 1];
      if (previous) return `${previous.url}?exitReport=${id}`;
    }
    return context.getReturnLink?.(id) ?? '';
  };

  const getNamePromise = (type: string, id: string) => getEntityNameApiRequest(type, id);

  const getKey = (type: string, id: string) => `${type}_${id}`;

  const updateName = (type: string, id: string) => {
    const needUpdate = tableQueryApi.tableActiveReports.value.some((tab: { id: string; drill?: { id: string }[] }) => {
      if (tab.id === id) {
        return true;
      }

      if (tab.drill) {
        return tab.drill.some((item) => item.id === id);
      }

      return false;
    });

    if (!needUpdate) {
      return Promise.resolve();
    }

    return getNamePromise(type, id).then((response) => {
      const key = getKey(type, id);
      cacheResponse(key, response);

      $emitter.emit('entity-change-name', {
        type,
        id,
        name: response.data.payload.name,
        nameFull: response.data.payload.nameFull,
      });
    });
  };

  const getDefaultReportNavigationOptions = (): ReportNavigationOptions => ({
    isAddTab: false,
    isReplaceTab: false,
    tabData: null,
  });

  const resolveReportNavigation = (
    drilldown: Bemob.ReportQueryDrill[],
    reportNavigationOptions: ReportNavigationOptions = {},
  ) => {
    const navigationOptions = {
      ...getDefaultReportNavigationOptions(),
      ...reportNavigationOptions,
    };
    const { isAddTab, isReplaceTab, isInit, tabData } = navigationOptions;

    store.setTableCustomGroups(null);

    const getId = (id: unknown, mix: unknown) => {
      if (isUUID(String(id))) {
        return id;
      }
      return `${id}${mix}`;
    };

    const expandedDrilldown: ExpandedReportQueryDrill[] = drilldown
      .filter((item): item is Bemob.ReportQueryDrill => Boolean(item))
      .map((item) => {
        const filterValues = item.filterValues == null ? '' : String(item.filterValues);
        if (filterValues.includes(DelimitersEnum.drillFilterOptions)) {
          const keys = filterValues.split(DelimitersEnum.drillFilterOptions);
          return keys.map<ExpandedReportQueryDrill>((key) => ({
            ...item,
            parentFilterValues: keys[0],
            filterValues: key,
          }));
        }
        return [{ ...item, filterValues }] as ExpandedReportQueryDrill[];
      })
      .reduce<ExpandedReportQueryDrill[]>((accumulator, items) => accumulator.concat(items), []);

    store.setTableDrilldownFilter(drilldown);

    const resolvedNames = expandedDrilldown.map((params) => {
      if (isUUID(params.filterValues)) {
        const cached = getCachedResponse(params.filterValues);
        const key = getKey(params.filterTables, params.filterValues);

        if (cached) {
          if (namePromisesCollection[key]) {
            delete namePromisesCollection[key];
          }

          return Promise.resolve(cached);
        }

        if (!namePromisesCollection[key]) {
          namePromisesCollection[key] = getNamePromise(params.filterTables, params.filterValues);
        }

        return namePromisesCollection[key].then((data) => {
          cacheResponse(params.filterValues, data);
          return data;
        });
      }

      return Promise.resolve({
        data: {
          payload: {
            id: getId(params.filterValues, params.filterTables),
            name: String(t(params.filterValues)),
          },
        },
      });
    });

    return Promise.all(resolvedNames).then((result) => {
      const namedResults = result as EntityNameResult[];
      if (drilldown.length === 0) {
        $emitter.emit('report-tab-updated');
      }

      const drill = drilldown.filter(Boolean).map((item, drilldownIndex) => {
        let filterValues = item.filterValues == null ? '' : String(item.filterValues);
        if (filterValues.includes(DelimitersEnum.drillFilterOptions)) {
          const keys = filterValues.split(DelimitersEnum.drillFilterOptions);
          filterValues = keys[0];
        }

        const index = expandedDrilldown.findIndex((drillItem) => drillItem.filterValues === filterValues);
        const children = expandedDrilldown.filter(
          (drillItem) => drillItem.parentFilterValues === filterValues,
        );

        const resolvedName = namedResults[index].data.payload;

        const keys = Object.keys(resolvedName.trafficSource || resolvedName);
        if (keys.indexOf('custom1Name') !== -1) {
          const attrs = keys.filter((key) => key.startsWith('custom') && key.endsWith('Name'));
          const tableCustomGroups = pick(resolvedName.trafficSource || resolvedName, attrs);
          store.setTableCustomGroups(tableCustomGroups);
        }

        const link = context.reportTabLink;

        if (index === 0) {
          store.addTableReportTab({
            id: getId(resolvedName.id, item.filterTables),
            drill: namedResults.map((drillResult) => drillResult.data.payload),
            title: (resolvedName.nameFull || resolvedName.name) as string,
            url: link,
            icon: 'fas fa-table',
            filterTables: get(drilldown, '[0].filterTables'),
            isAddTab,
            isReplaceTab,
            get returnLink() {
              return tabReturnLink(resolvedName.id as string);
            },
            tabData,
          });
          store.setTableCurrentReport(resolvedName.id);
          if (isInit) {
            $emitter.emit('report-tab-added');
          } else {
            $emitter.emit('report-tab-updated');
          }
        }

        return {
          index: drilldownIndex,
          last: drilldownIndex === drilldown.length - 1,
          id: resolvedName.id,
          text: (resolvedName.nameFull || resolvedName.name) as string,
          filterTables: item.filterTables,
          filterValues: resolvedName.id,
          filterOptions: item.filterOptions,
          filterOffset: item.filterOffset,
          filterLimit: item.filterLimit,
          workspaceId: resolvedName.workspaceId,
          children: children.map((child) => {
            const childIndex = expandedDrilldown.findIndex(
              (drillItem) => drillItem.filterValues === child.filterValues,
            );
            const childResolvedName = namedResults[childIndex].data.payload;

            return {
              ...child,
              text: (childResolvedName.nameFull || childResolvedName.name) as string,
            };
          }),
        };
      });

      context.drill = drill;
      store.setTableDrilldown(drill);
    });
  };

  return {
    errorToast,
    isUUID,
    cacheResponse,
    getCachedResponse,
    tabReturnLink,
    getNamePromise,
    getKey,
    updateName,
    getDefaultReportNavigationOptions,
    resolveReportNavigation,
  };
};
